import React, { useEffect, useState } from 'react';
import { Card } from '../../../components/base/Card';
import { Button } from '../../../components/base/Button';
import { Link } from 'react-router-dom';
import { SEO, getServiceSchema } from '../../../utils/seo';
import { supabase } from '../../../lib/supabase';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  icon: string;
  slug: string;
  image_url: string;
  is_active: boolean;
  content?: string;
}

export const KaynakPage: React.FC = () => {
  const [service, setService] = useState<Service | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchService();
  }, []);

  const fetchService = async () => {
    try {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('slug', 'kaynak')
        .eq('is_active', true)
        .single();

      if (error) {
        console.error('Hizmet yüklenirken hata:', error);
        setService(null);
      } else if (data) {
        setService(data);
      }
    } catch (error) {
      console.error('Hizmet yüklenirken hata:', error);
      setService(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#C1121F]"></div>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Hizmet bulunamadı</h2>
          <p className="text-gray-600 mb-6">Aradığınız hizmet mevcut değil veya kaldırılmış olabilir.</p>
          <Link to="/hizmetler">
            <Button>Hizmetlere Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  const serviceSchema = getServiceSchema(service.title, service.description);

  return (
    <div className="min-h-screen">
      <SEO
        title={`${service.title} | 3DP Ankara`}
        description={service.description}
        keywords={`${service.title}, CNC, hassas imalat, metal işleme`}
        canonical={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hizmetler/${service.slug}`}
        ogTitle={`${service.title} | 3DP Ankara`}
        ogDescription={service.description}
        ogUrl={`${import.meta.env.VITE_SITE_URL || "https://example.com"}/hizmetler/${service.slug}`}
        jsonLd={serviceSchema}
      />

      {/* Hero Section */}
      <section 
        className="relative h-96 bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(22, 22, 22, 0.7), rgba(22, 22, 22, 0.7)), url('${service.image_url}')`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <div className="flex items-center mb-6">
              <div className="w-16 h-16 bg-[#C1121F] rounded-xl flex items-center justify-center mr-4">
                <i className={`${service.icon} text-white text-2xl`}></i>
              </div>
              <h1 className="text-5xl font-bold" style={{ fontFamily: '"Poppins", sans-serif' }}>
                {service.title}
              </h1>
            </div>
            <p className="text-xl leading-relaxed">
              {service.description}
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      {service.content && (
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div 
                className="prose prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: service.content }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#161616] mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
              Özellikler ve Avantajlar
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {service.features && service.features.length > 0 ? (
              service.features.map((feature, index) => (
                <Card key={index} hover className="text-center">
                  <div className="w-16 h-16 bg-[#C1121F] rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-white text-2xl"></i>
                  </div>
                  <p className="text-gray-700 font-medium">{feature}</p>
                </Card>
              ))
            ) : (
              <div className="col-span-3 text-center text-gray-500">
                Henüz özellik eklenmemiş.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#C1121F]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6" style={{ fontFamily: '"Poppins", sans-serif' }}>
            Projeniz İçin Hemen Teklif Alın
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Teknik çiziminizi gönderin, aynı gün içinde detaylı teklif alın.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/teklif-al">
              <Button variant="secondary" size="lg">
                <i className="ri-file-upload-line mr-2"></i>
                Çizim Yükle & Teklif Al
              </Button>
            </Link>
            <Link to="/iletisim">
              <Button variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-[#C1121F]">
                <i className="ri-phone-line mr-2"></i>
                Hemen Ara
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
